<template>
  <v-container fluid class="pa-6 pt-3">
    <v-card class="mb-5 filter-card" elevation="0">
      <v-card-text class="pa-4">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="displayDate"
                  readonly
                  outlined
                  dense
                  clearable
                  label="Date Range"
                  prepend-inner-icon="mdi-calendar"
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="resetDate"
                />
              </template>

              <v-card>
                <v-card-text class="pa-3">
                  <v-row no-gutters>
                    <v-col>
                      <v-date-picker
                        v-model="selectedDates"
                        range
                        @input="onDateChange"
                      />
                    </v-col>
                    <v-col class="pl-3 pt-3 quick-panel">
                      <v-btn
                        v-for="option in dateOptions"
                        :key="option.label"
                        small
                        text
                        block
                        color="accent"
                        class="quick-date-btn"
                        @click="setQuickDate(option)"
                      >
                        {{ option.label }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="form.types"
              :items="feeTypes"
              outlined
              dense
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              label="Particular"
              item-text="text"
              item-value="id"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="form.program_id"
              :items="programs"
              :item-text="getProgramDisplayName"
              item-value="id"
              outlined
              dense
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              label="Programme"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2" v-if="showPartner()">
            <v-autocomplete
              v-model="form.partner_id"
              :items="partners"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              label="Partner"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="auto">
            <v-btn
              color="accent"
              class="white--text"
              :loading="loading"
              @click="getReport"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Search
            </v-btn>

            <v-btn
              color="accent"
              class="white--text ml-2"
              :disabled="downloadLoading || students.length === 0"
              :loading="downloadLoading"
              @click="downloadAuditReport"
            >
              <v-icon left small>mdi-download</v-icon>
              Download
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row class="mb-5">
      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card" elevation="1">
          <v-card-text>
            <div class="summary-label">Total Records</div>
            <div class="summary-value">{{ formatNumber(students.length) }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card" elevation="1">
          <v-card-text>
            <div class="summary-label">Total Amount</div>
            <div class="summary-value">
              {{ formatAmount(grandTotalAmount) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card elevation="1">
      <v-data-table
        :headers="headers"
        :items="students"
        :loading="loading"
        :items-per-page="10"
        fixed-header
        class="student-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, -1],
          'show-first-last-page': true,
        }"
      >
        <template v-slot:item.usn_id="{ item }">
          <span>{{ item.usn_id || "-" }}</span>
        </template>

        <template v-slot:item.receipt_number="{ item }">
          <span>{{ item.receipt_number || "-" }}</span>
        </template>

        <template v-slot:item.particular="{ item }">
          <v-chip
            small
            :color="getTypeColor(item.particular)"
            text-color="white"
          >
            {{ item.particular || "-" }}
          </v-chip>
        </template>

        <template v-slot:item.program_id="{ item }">
          {{ getProgramNameById(item.program_id) }}
        </template>

        <template v-slot:item.partner_id="{ item }">
          {{ getPartnerNameById(item.partner_id) }}
        </template>

        <template v-slot:item.term_no="{ item }">
          {{ item.term_no || "-" }}
        </template>

        <template v-slot:item.amount="{ item }">
          <span class="font-weight-bold green--text text--darken-2">
            {{ formatAmount(item.amount) }}
          </span>
        </template>

        <template v-slot:item.transaction_date="{ item }">
          {{ formatDate(item.transaction_date) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small color="success" text-color="white">
            {{ item.status || "Completed" }}
          </v-chip>
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn icon small color="accent" @click="openStudentDialog(item)">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="studentDialog" max-width="760">
      <v-card v-if="selectedStudent">
        <v-card-title class="primary white--text">
          Payment Details
          <v-spacer />
          <v-btn icon dark @click="studentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <detail-row
                label="Application ID"
                :value="selectedStudent.application_id"
              />
              <detail-row label="USN" :value="selectedStudent.usn_id" />
              <detail-row label="Name" :value="selectedStudent.name" />
              <detail-row
                label="Mobile No"
                :value="selectedStudent.mobile_no"
              />
              <detail-row label="Email" :value="selectedStudent.email" />
            </v-col>

            <v-col cols="12" md="6">
              <detail-row
                label="Particular"
                :value="selectedStudent.particular"
              />
              <detail-row
                label="Amount"
                :value="formatAmount(selectedStudent.amount)"
              />
              <detail-row
                label="Receipt Number"
                :value="selectedStudent.receipt_number"
              />
              <detail-row
                label="Reference Number"
                :value="selectedStudent.transaction_id"
              />
              <detail-row
                label="Transaction Date"
                :value="formatDate(selectedStudent.transaction_date)"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  components: {
    DetailRow: {
      props: ["label", "value"],
      template: `
        <div class="mb-3">
          <div class="caption grey--text text--darken-1">{{ label }}</div>
          <div class="body-2 font-weight-medium">{{ value || "-" }}</div>
        </div>
      `,
    },
  },

  data() {
    return {
      menu: false,
      selectedDates: [],
      displayDate: "",
      loading: false,
      downloadLoading: false,
      students: [],
      programs: [],
      partners: [],
      selectedStudent: null,
      studentDialog: false,
      form: {
        from_date: "",
        to_date: "",
        types: [],
        program_id: [],
        partner_id: [],
      },
      feeTypes: [
        { id: "Advance Tuition Fee", text: "Tuition Fee Advance" },
        { id: "Tuition Fee", text: "Tuition Fee" },
        { id: "Exam Fee", text: "Exam Fee" },
        { id: "Re-Admission Fee", text: "Re-Admission Fee" },
        { id: "Convocation Fee", text: "Convocation Fee" },
        { id: "Postal Fee", text: "Postal Fee" },
        { id: "Add-on Programme", text: "Add-on Programme" },
      ],
      dateOptions: [
        { label: "Today", days: 0 },
        { label: "Yesterday", type: "yesterday" },
        { label: "This Week", type: "week" },
        { label: "Last 7 Days", days: 6 },
        { label: "This Month", type: "month" },
        { label: "Last 30 Days", days: 29 },
        { label: "Reset", type: "reset" },
      ],
      headers: [
        { text: "Application ID", value: "application_id" },
        { text: "USN", value: "usn_id" },
        { text: "Name", value: "name" },
        { text: "Mobile No", value: "mobile_no" },
        { text: "Email", value: "email" },
        { text: "Payment Term", value: "term_no" },
        { text: "Program", value: "program_id" },
        { text: "Partner", value: "partner_id" },
        { text: "Particular", value: "particular" },
        { text: "Receipt Number", value: "receipt_number" },
        { text: "Transaction ID", value: "transaction_id" },
        { text: "Transaction Date", value: "transaction_date" },
        { text: "Amount(₹)", value: "amount" },
      ],
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    grandTotalAmount() {
      return this.students.reduce(
        (sum, row) => sum + parseFloat(row.amount || 0),
        0,
      );
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Consolidated Fee Report" }]);
    await this.getPrograms();
    await this.showPartnerFilter();
    await this.getReport();
  },

  methods: {
    async getReport() {
      this.loading = true;

      try {
        const queryParams = {
          from_date: this.form.from_date,
          to_date: this.form.to_date,
          fee_types: this.form.types.join(","),
          program_id: this.form.program_id.join(","),
          partner_id: this.form.partner_id.length
            ? this.form.partner_id.join(",")
            : null,
        };

        Object.keys(queryParams).forEach((key) => {
          if (!queryParams[key]) delete queryParams[key];
        });

        const response = await this.$axios.get(
          `/audit-service/consolidated-fee-payments?${this.objectToQueryParams(
            queryParams,
          )}`,
        );

        this.students = response.data.data || [];
      } catch (error) {
        console.error(error);
        this.$toast.error("Failed to load consolidated fee report");
      } finally {
        this.loading = false;
      }
    },

    async getPrograms() {
      const endpoint =
        this.currentUser.partner_id != 1
          ? `/get_partner_programs/${this.currentUser.partner_id}`
          : "/programs";

      const response = await this.$axios.get(endpoint);
      this.programs = response.data.data || [];
    },

    async getPartners() {
      const response = await this.$axios.get("/partners");
      this.partners = response.data.data || [];
    },

    getPartnerName(partner) {
      return partner ? partner.name : "";
    },

    getProgramNameById(id) {
      if (!id) return "-";
      const program = this.programs.find((p) => p.id == id);
      if (!program) return id;
      const shortName = program.degree?.short_name || "";
      return program.specialization
        ? `${shortName} - ${program.specialization}`
        : shortName || id;
    },

    getPartnerNameById(id) {
      if (!id) return "-";
      const partner = this.partners.find((p) => p.id == id);
      return partner ? partner.name : id;
    },

    async showPartnerFilter() {
      this.form.partner_id =
        this.currentUser.partner_id != 1 ? [this.currentUser.partner_id] : [];
      await this.getPartners();
    },

    showPartner() {
      return this.currentUser.partner_id == 1;
    },

    getProgramDisplayName(item) {
      if (!item) return "";
      let name = item.degree?.short_name || "";
      if (item.specialization) name += ` - ${item.specialization}`;
      return name;
    },

    getTypeColor(type) {
      // const colors = {
      //   "Application Fee": "deep-purple",
      //   "Tuition Fee Advance": "purple",
      //   "Tuition Fee": "blue",
      //   "Exam Fee": "orange",
      //   "Re-Admission Fee": "red",
      //   "Convocation Fee": "indigo",
      //   "Postal Fee": "teal",
      //   "Add-on Programme": "cyan",
      // };

      return "grey";
    },

    openStudentDialog(item) {
      this.selectedStudent = item;
      this.studentDialog = true;
    },

    async downloadAuditReport() {
      this.downloadLoading = true;

      try {
        const resolvedStudents = await Promise.all(
          this.students.map(async (row) => ({
            ...row,
            resolved_program_name: this.getProgramNameById(row.program_id),
            resolved_partner_name: this.getPartnerNameById(row.partner_id),
          })),
        );

        const displayHeaders = [
          { text: "Application ID", value: "application_id" },
          { text: "USN", value: "usn_id" },
          { text: "Name", value: "name" },
          { text: "Mobile No", value: "mobile_no" },
          { text: "Email", value: "email" },
          { text: "Payment Term", value: "term_no" },
          { text: "Program", value: "resolved_program_name" },
          { text: "Partner", value: "resolved_partner_name" },
          { text: "Particular", value: "particular" },
          { text: "Payment Status", value: "type" },
          { text: "Receipt Number", value: "receipt_number" },
          { text: "Transaction ID", value: "transaction_id" },
          { text: "Transaction Date", value: "transaction_date" },
          { text: "Amount", value: "amount" },
        ];

        const rows = resolvedStudents.map((row) =>
          displayHeaders
            .map((header) => {
              let value = row[header.value];

              if (header.value === "amount") {
                value = this.formatAmount(value);
              } else if (header.value === "transaction_date") {
                value = this.formatDate(value);
              }

              return `"${String(value || "").replace(/"/g, '""')}"`;
            })
            .join(","),
        );

        const csv = [displayHeaders.map((h) => h.text).join(","), ...rows].join(
          "\n",
        );

        const blob = new Blob(["\uFEFF" + csv], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute(
          "download",
          `Consolidated_Fee_Report_${new Date()
            .toISOString()
            .slice(0, 10)}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading report:", error);
        this.$toast.error("Failed to download report");
      } finally {
        this.downloadLoading = false;
      }
    },

    formatAmount(amount) {
      const value = parseFloat(amount || 0);
      return new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
      }).format(value);
    },

    formatNumber(num) {
      return new Intl.NumberFormat("en-IN").format(num || 0);
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}-${d.getFullYear()}`;
    },

    formatDateBackend(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;
    },

    onDateChange() {
      if (this.selectedDates.length === 2) {
        const dates = [...this.selectedDates].sort();
        this.form.from_date = dates[0];
        this.form.to_date = dates[1];
        this.displayDate = `${this.formatDate(dates[0])} - ${this.formatDate(
          dates[1],
        )}`;
      }
    },

    setQuickDate(option) {
      if (option.type === "reset") {
        this.resetDate();
        return;
      }

      const today = new Date();
      const fromDate = new Date(today);
      const toDate = new Date(today);

      if (option.type === "week")
        fromDate.setDate(today.getDate() - today.getDay());
      else if (option.type === "month") fromDate.setDate(1);
      else if (option.type === "yesterday") {
        fromDate.setDate(today.getDate() - 1);
        toDate.setDate(today.getDate() - 1);
      } else if (option.days !== undefined) {
        fromDate.setDate(today.getDate() - option.days);
      }

      this.selectedDates = [
        this.formatDateBackend(fromDate),
        this.formatDateBackend(toDate),
      ];
      this.onDateChange();
    },

    resetDate() {
      this.selectedDates = [];
      this.displayDate = "";
      this.form.from_date = "";
      this.form.to_date = "";
    },

    objectToQueryParams(obj) {
      return Object.keys(obj)
        .map(
          (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
        )
        .join("&");
    },
  },
};
</script>

<style scoped>
.filter-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.quick-panel {
  max-width: 150px;
}

.quick-date-btn {
  justify-content: flex-start;
  text-transform: none;
}

.summary-card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.summary-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.summary-value {
  color: #0f172a;
  font-size: 26px;
  font-weight: 700;
  margin-top: 4px;
}

.student-table {
  border-top: 1px solid #eeeeee;
}

.v-data-table >>> th {
  font-weight: 700 !important;
  white-space: nowrap;
}

.v-data-table >>> td {
  white-space: nowrap;
}

@media (max-width: 960px) {
  .summary-value {
    font-size: 22px;
  }
}
</style>
